import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/system';
import { useCasinoLinkAction, type CasinoLinkAction } from '../../Casino/CasinoHelper';
import { createChatSession } from '../../Chat/ChatSession';
import type { ChatSessionResult, CreateChatSessionInput } from '../../Chat/Type/ChatTypes';
import { getCasinoRace } from '../../CasinoRace/CasinoRaceData';
import { IS_CASINO_RACE_DEBUG_ENABLED, shuffleCasinoRaceForDebug } from '../../CasinoRace/CasinoRaceDebug';
import type { CasinoRaceDataResult, CasinoRaceLeaderboardEntry } from '../../CasinoRace/Type/CasinoRaceTypes';
import { getCurrentLanguage, t } from '../../i18n';

type ChatMessageType =
  | 'hs.chat.loaded'
  | 'hs.chat.resize'
  | 'hs.chat.closed'
  | 'hs.chat.openWheel'
  | 'hs.chat.openCasinoRace'
  | 'hs.chat.openPokerCashTable'
  | 'hs.chat.openPokerTournament'
  | 'hs.chat.casinoLink'
  | 'hs.chat.detach';

type ChatMessage = {
  type: ChatMessageType;
  chatSessionId?: string;
  width?: number;
  height?: number;
  roomId?: string;
  gameId?: string;
  casinoAction?: CasinoLinkAction;
};

type CasinoRaceRequestOptions = {
  showLoading?: boolean;
};

type CasinoRaceDebugApi = {
  help: () => true;
  open: () => void;
  refresh: () => void;
  shuffle: () => CasinoRaceDataResult;
};

export type ChatFrameSize = {
  width?: number;
  height?: number;
};

export type PokerLinkCatalogItem = {
  id: string;
  name: string;
  meta?: string;
  imageName?: string;
};

export type PokerLinkCatalog = {
  cashTables?: PokerLinkCatalogItem[];
  tournaments?: PokerLinkCatalogItem[];
};

export interface CommunityChatPanelProps {
  active?: boolean;
  input?: CreateChatSessionInput;
  title?: string;
  className?: string;
  sx?: SxProps<Theme>;
  pokerLinkCatalog?: PokerLinkCatalog;
  onClose?: () => void;
  onLoaded?: (session: ChatSessionResult | null) => void;
  onOpenWheel?: () => void;
  onOpenCasinoRace?: () => void;
  onOpenPokerCashTable?: (roomId: string) => void;
  onOpenPokerTournament?: (gameId: string) => void;
  onResize?: (size: ChatFrameSize) => void;
  onDetach?: () => void;
  closeOnChatClosed?: boolean;
}

const CHAT_MESSAGE_TYPES = new Set<ChatMessageType>([
  'hs.chat.loaded',
  'hs.chat.resize',
  'hs.chat.closed',
  'hs.chat.openWheel',
  'hs.chat.openCasinoRace',
  'hs.chat.openPokerCashTable',
  'hs.chat.openPokerTournament',
  'hs.chat.casinoLink',
  'hs.chat.detach',
]);

const DEFAULT_CHAT_WIDTH = 400;
const DEFAULT_CHAT_HEIGHT = 640;
const MIN_CHAT_WIDTH = 320;
const MAX_CHAT_WIDTH = 480;
const MIN_CHAT_HEIGHT = 420;
const MAX_CHAT_HEIGHT = 1200;
const CHAT_SESSION_REFRESH_SKEW_MS = 30_000;
const MIN_CHAT_SESSION_REFRESH_DELAY_MS = 5_000;
const CHAT_EMBED_READY_TIMEOUT_MS = 10_000;
const CHAT_UNAVAILABLE_MESSAGE = 'Community chat is currently not available.';
const CASINO_RACE_UNAVAILABLE_MESSAGE = 'Casino race is currently not available.';
const CASINO_RACE_REFRESH_INTERVAL_MS = 15_000;
const CASINO_RACE_LEADERBOARD_LIMIT = 250;
const CASINO_RACE_ROW_REORDER_DURATION_MS = 220;
const CASINO_RACE_ROW_REORDER_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
const CASINO_RACE_ROW_TRANSITION = `transform ${CASINO_RACE_ROW_REORDER_DURATION_MS}ms ${CASINO_RACE_ROW_REORDER_EASING}`;

const CASINO_RACE_CLOSE_BUTTON_SX = {
  color: '#FFFFFF',
  bgcolor: '#232530',
  border: '1px solid rgba(255, 255, 255, 0.14)',
  borderRadius: '6px',
  height: 30,
  width: 30,
  '&:hover': {
    bgcolor: '#d12b3a',
    borderColor: '#d12b3a',
  },
} as const;

const getPositiveNumber = (value: unknown): number | undefined => {
  const numericValue = typeof value === 'string' ? Number(value) : value;
  return typeof numericValue === 'number' && Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined;
};

const getBoundedDimension = (value: number | undefined, fallback: number, min: number, max: number): number => {
  if (!value) return fallback;
  return Math.min(max, Math.max(min, Math.ceil(value)));
};

const getOptionalString = (value: unknown): string | undefined => (
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined
);

const parseCasinoLinkAction = (value: unknown): CasinoLinkAction | undefined => {
  if (!value || typeof value !== 'object') return undefined;

  const action = value as {
    type?: unknown;
    id?: unknown;
    slug?: unknown;
    title?: unknown;
    image?: unknown;
    providerId?: unknown;
    demo?: unknown;
  };

  const type = action.type;
  const id = getOptionalString(action.id);
  const slug = getOptionalString(action.slug);

  if (type === 'provider') {
    if (!id && !slug) return undefined;
    return { type, id, slug };
  }

  if (type === 'category') {
    if (!id && !slug) return undefined;
    return { type, id, slug };
  }

  if (type === 'game') {
    if (!slug) return undefined;
    return {
      type,
      slug,
      title: getOptionalString(action.title),
      image: getOptionalString(action.image) || null,
      providerId: getOptionalString(action.providerId),
      demo: typeof action.demo === 'boolean' ? action.demo : undefined,
    };
  }

  return undefined;
};

const parseChatMessage = (data: unknown): ChatMessage | null => {
  const parsedData = typeof data === 'string'
    ? (() => {
        try {
          return JSON.parse(data);
        } catch {
          return null;
        }
      })()
    : data;

  if (!parsedData || typeof parsedData !== 'object') return null;

  const type = (parsedData as { type?: unknown }).type;
  if (typeof type !== 'string') return null;
  if (!CHAT_MESSAGE_TYPES.has(type as ChatMessageType)) return null;

  const chatSessionId = (parsedData as { chatSessionId?: unknown }).chatSessionId;
  const rawWidth = (parsedData as { width?: unknown }).width;
  const rawHeight = (parsedData as { height?: unknown }).height;
  const roomId = getOptionalString((parsedData as { roomId?: unknown }).roomId);
  const gameId = getOptionalString((parsedData as { gameId?: unknown }).gameId);
  const casinoAction = parseCasinoLinkAction((parsedData as { casinoAction?: unknown }).casinoAction);

  return {
    type: type as ChatMessageType,
    chatSessionId: typeof chatSessionId === 'string' && chatSessionId.length > 0 ? chatSessionId : undefined,
    width: getPositiveNumber(rawWidth),
    height: getPositiveNumber(rawHeight),
    roomId,
    gameId,
    casinoAction,
  };
};

const getValidatedChatUrl = (chatUrl?: string | null): string | null => {
  if (!chatUrl) return null;

  try {
    const url = new URL(chatUrl);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.toString();
  } catch {
    return null;
  }
};

const getOriginFromUrl = (url?: string | null): string | null => {
  if (!url) return null;

  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

const normalizePokerLinkCatalogItems = (items?: PokerLinkCatalogItem[]): PokerLinkCatalogItem[] => {
  if (!Array.isArray(items)) return [];

  const normalizedItems: PokerLinkCatalogItem[] = [];

  for (const item of items) {
    const id = getOptionalString(item?.id);
    const name = getOptionalString(item?.name);
    if (!id || !name) continue;

    normalizedItems.push({
      id,
      name,
      meta: getOptionalString(item?.meta),
      imageName: getOptionalString(item?.imageName),
    });

    if (normalizedItems.length >= 250) break;
  }

  return normalizedItems;
};

const normalizePokerLinkCatalog = (catalog?: PokerLinkCatalog): Required<PokerLinkCatalog> => ({
  cashTables: normalizePokerLinkCatalogItems(catalog?.cashTables),
  tournaments: normalizePokerLinkCatalogItems(catalog?.tournaments),
});

const buildChatSessionInput = (input?: CreateChatSessionInput): CreateChatSessionInput | undefined => {
  const nextInput: CreateChatSessionInput = { ...(input || {}) };

  if (!nextInput.lang) {
    nextInput.lang = getCurrentLanguage();
  }

  if (!nextInput.displayMode) {
    nextInput.displayMode = 'embedded';
  }

  return Object.keys(nextInput).length > 0 ? nextInput : undefined;
};

const getSessionRefreshDelayMs = (session: ChatSessionResult): number | null => {
  if (typeof session.ttlSeconds === 'number' && Number.isFinite(session.ttlSeconds) && session.ttlSeconds > 0) {
    return Math.max(
      MIN_CHAT_SESSION_REFRESH_DELAY_MS,
      session.ttlSeconds * 1000 - CHAT_SESSION_REFRESH_SKEW_MS,
    );
  }

  if (session.expiresAt) {
    const expiresAtMs = Date.parse(session.expiresAt);
    if (Number.isFinite(expiresAtMs)) {
      return Math.max(
        MIN_CHAT_SESSION_REFRESH_DELAY_MS,
        expiresAtMs - Date.now() - CHAT_SESSION_REFRESH_SKEW_MS,
      );
    }
  }

  return null;
};

const formatNumber = (value?: number | null): string => (
  typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)
    : '-'
);

const formatPrize = (entry?: CasinoRaceLeaderboardEntry | null): string => {
  const prize = entry?.prize;
  if (!prize) return '-';
  if (prize.totalNumber !== null && typeof prize.totalNumber === 'number') {
    return `$${formatNumber(prize.totalNumber)}`;
  }
  if (prize.total) {
    return `$${prize.total}`;
  }
  return '-';
};

const AvatarBubble: React.FC<{ entry: CasinoRaceLeaderboardEntry; size?: number }> = ({ entry, size = 30 }) => (
  <Avatar
    alt={`${entry.username || 'User'} avatar`}
    src={entry.avatarUrl || undefined}
    sx={{
      bgcolor: '#2A2C37',
      border: entry.isCurrentUser ? '2px solid #F93751' : '1px solid rgba(255,255,255,0.10)',
      color: 'rgba(255,255,255,0.78)',
      flex: `0 0 ${size}px`,
      fontSize: Math.max(10, Math.floor(size / 2.8)),
      fontWeight: 800,
      height: size,
      width: size,
    }}
  />
);

const formatCurrentRaceDate = (): string => (
  new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date())
);

const getCasinoRaceEntryKey = (entry: CasinoRaceLeaderboardEntry): string => (
  entry.userId || entry.username
);

const CASINO_RACE_USERNAME_VISIBLE_CHARS = 3;

const MaskedUsername: React.FC<{ username: string }> = ({ username }) => {
  const visible = username.slice(0, CASINO_RACE_USERNAME_VISIBLE_CHARS);
  const hidden = username.slice(CASINO_RACE_USERNAME_VISIBLE_CHARS);

  if (!hidden) return <>{visible}</>;

  return (
    <>
      {visible}
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          display: 'inline-block',
          filter: 'blur(4px)',
          opacity: 0.85,
          pointerEvents: 'none',
          userSelect: 'none',
          verticalAlign: 'bottom',
        }}
      >
        {hidden}
      </Box>
    </>
  );
};

const CasinoRaceRow: React.FC<{ entry: CasinoRaceLeaderboardEntry }> = ({ entry }) => (
  <Box
    data-flip-key={getCasinoRaceEntryKey(entry)}
    sx={{
      alignItems: 'center',
      bgcolor: entry.isCurrentUser ? 'rgba(249, 55, 81, 0.16)' : '#1C1E26',
      border: entry.isCurrentUser ? '1px solid rgba(249, 55, 81, 0.50)' : '1px solid rgba(255,255,255,0.07)',
      borderRadius: '6px',
      display: 'grid',
      gap: 1,
      gridTemplateColumns: '34px minmax(0, 1fr) auto',
      minHeight: 52,
      px: 1,
      py: 0.75,
    }}
  >
    <Typography sx={{ color: entry.isCurrentUser ? '#FFFFFF' : 'rgba(255,255,255,0.72)', fontSize: 13, fontWeight: 900 }}>
      #{entry.rank}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      <AvatarBubble entry={entry} />
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ color: '#FFFFFF', fontSize: 13, fontWeight: 800 }}>
          {entry.isCurrentUser ? 'You' : <MaskedUsername username={entry.username} />}
        </Typography>
        <Typography noWrap sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 11, fontWeight: 600 }}>
          {formatNumber(entry.amount)} pts
        </Typography>
      </Box>
    </Stack>
    <Typography sx={{ color: '#FFC857', fontSize: 12, fontWeight: 900, textAlign: 'right' }}>
      {formatPrize(entry)}
    </Typography>
  </Box>
);

const CasinoRaceLeaderboard: React.FC<{ entries: CasinoRaceLeaderboardEntry[] }> = ({ entries }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevPositionsRef = useRef<Map<string, number>>(new Map());

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const children = (Array.from(container.children) as HTMLElement[]).filter(
      (node) => Boolean(node.dataset.flipKey),
    );

    children.forEach((child) => {
      if (child.style.transform || child.style.transition) {
        child.style.transition = 'none';
        child.style.transform = '';
      }
    });

    const newPositions = new Map<string, number>();
    children.forEach((child) => {
      const key = child.dataset.flipKey as string;
      newPositions.set(key, child.offsetTop);
    });

    const prevPositions = prevPositionsRef.current;
    const moved: HTMLElement[] = [];
    children.forEach((child) => {
      const key = child.dataset.flipKey as string;
      const prevTop = prevPositions.get(key);
      const newTop = newPositions.get(key) as number;
      if (prevTop === undefined) return;
      const delta = prevTop - newTop;
      if (delta === 0) return;
      child.style.transform = `translateY(${delta}px)`;
      child.style.willChange = 'transform';
      moved.push(child);
    });

    prevPositionsRef.current = newPositions;

    if (moved.length === 0) return undefined;

    void container.offsetHeight;

    const frameId = window.requestAnimationFrame(() => {
      moved.forEach((child) => {
        child.style.transition = CASINO_RACE_ROW_TRANSITION;
        child.style.transform = '';
      });
    });

    const handlers = moved.map((child) => {
      const onTransitionEnd = (event: TransitionEvent) => {
        if (event.propertyName !== 'transform') return;
        child.style.transition = '';
        child.style.willChange = '';
        child.removeEventListener('transitionend', onTransitionEnd);
      };
      child.addEventListener('transitionend', onTransitionEnd);
      return { child, onTransitionEnd };
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      handlers.forEach(({ child, onTransitionEnd }) => {
        child.removeEventListener('transitionend', onTransitionEnd);
      });
    };
  }, [entries]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
      }}
    >
      {entries.map((entry) => (
        <CasinoRaceRow key={getCasinoRaceEntryKey(entry)} entry={entry} />
      ))}
    </Box>
  );
};

const CasinoRacePanel: React.FC<{
  race: CasinoRaceDataResult | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onRetry: () => void;
}> = ({ race, loading, error, onClose, onRetry }) => {
  const currentUser = race?.currentUser;
  const currentRaceDate = useMemo(() => formatCurrentRaceDate(), []);
  const leaderboardEntries = useMemo(
    () => race?.leaderboard.slice(0, CASINO_RACE_LEADERBOARD_LIMIT) ?? [],
    [race?.leaderboard],
  );

  return (
    <Box
      sx={{
        bgcolor: '#14151A',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        inset: 0,
        position: 'absolute',
        zIndex: 10,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={1}
        sx={{
          background: race
            ? 'linear-gradient(135deg, rgba(249, 55, 81,0.18), rgba(255,200,87,0.08))'
            : undefined,
          borderBottom: '1px solid rgba(255,255,255,0.10)',
          flex: '0 0 auto',
          px: 1.25,
          py: 1,
        }}
      >
        <EmojiEventsIcon sx={{ color: '#FFC857', fontSize: 20, mt: '2px' }} />
        <Stack spacing={0.5} sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Typography noWrap sx={{ color: '#FFFFFF', fontSize: 14, fontWeight: 900, lineHeight: 1.2 }}>
            {race?.title || 'Casino race'}
          </Typography>
          {race ? (
            <Stack direction="row" alignItems="center" spacing={0.6} sx={{ flexWrap: 'wrap', rowGap: 0.4 }}>
              <Typography sx={{ bgcolor: '#F93751', borderRadius: '4px', color: '#FFFFFF', fontSize: 10, fontWeight: 900, px: 0.7, py: 0.25, textTransform: 'uppercase' }}>
                {race.status || 'race'}
              </Typography>
              <Typography sx={{ bgcolor: '#242632', borderRadius: '4px', color: 'rgba(255,255,255,0.76)', fontSize: 10, fontWeight: 800, px: 0.7, py: 0.25 }}>
                {race.totalEntries} players
              </Typography>
              <Typography noWrap sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 11, fontWeight: 600 }}>
                {currentRaceDate}
              </Typography>
            </Stack>
          ) : (
            <Typography noWrap sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 11, fontWeight: 600 }}>
              {currentRaceDate}
            </Typography>
          )}
        </Stack>
        <IconButton aria-label={t('common:actions.close')} onClick={onClose} sx={CASINO_RACE_CLOSE_BUTTON_SX}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {loading ? (
        <Stack sx={{ flex: '1 1 auto', p: 3 }} alignItems="center" justifyContent="center" spacing={2}>
          <CircularProgress size={28} />
          <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 14 }}>
            Loading casino race...
          </Typography>
        </Stack>
      ) : error ? (
        <Stack sx={{ flex: '1 1 auto', p: 3 }} alignItems="stretch" justifyContent="center" spacing={2}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={onRetry}>
            Retry
          </Button>
        </Stack>
      ) : race ? (
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            p: 1.25,
            scrollbarColor: '#F93751 #2a2b33',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { width: 8 },
            '&::-webkit-scrollbar-track': { bgcolor: '#2a2b33' },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: '#F93751',
              border: '2px solid #2a2b33',
              borderRadius: 99,
            },
          }}
        >
          <Stack spacing={1.15}>
            <Box
              sx={{
                bgcolor: currentUser ? 'rgba(249, 55, 81, 0.14)' : '#1C1E26',
                border: currentUser ? '1px solid rgba(249, 55, 81, 0.48)' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: '8px',
                p: 1.25,
              }}
            >
              <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 10, fontWeight: 800, mb: 0.7, textTransform: 'uppercase' }}>
                Your position
              </Typography>
              {currentUser ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AvatarBubble entry={currentUser} size={36} />
                  <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                    <Typography sx={{ color: '#FFFFFF', fontSize: 14, fontWeight: 900 }}>
                      #{currentUser.rank}
                    </Typography>
                    <Typography noWrap sx={{ color: 'rgba(255,255,255,0.70)', fontSize: 12, fontWeight: 700 }}>
                      {formatNumber(currentUser.amount)} pts
                    </Typography>
                  </Box>
                  <Typography sx={{ color: '#FFC857', fontSize: 13, fontWeight: 900 }}>
                    {formatPrize(currentUser)}
                  </Typography>
                </Stack>
              ) : (
                <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 13, fontWeight: 700, lineHeight: 1.35 }}>
                  Start playing casino games to earn points and win cash prizes.
                </Typography>
              )}
            </Box>

            <Stack spacing={0.75}>
              <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>
                Leaderboard
              </Typography>
              {leaderboardEntries.length > 0 ? (
                <CasinoRaceLeaderboard entries={leaderboardEntries} />
              ) : (
                <Typography sx={{ bgcolor: '#1C1E26', borderRadius: '6px', color: 'rgba(255,255,255,0.70)', fontSize: 13, p: 1.25 }}>
                  No players in this race yet.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      ) : (
        <Stack sx={{ flex: '1 1 auto', p: 3 }} alignItems="center" justifyContent="center">
          <Alert severity="error">{CASINO_RACE_UNAVAILABLE_MESSAGE}</Alert>
        </Stack>
      )}
    </Box>
  );
};

const CommunityChatPanel: React.FC<CommunityChatPanelProps> = ({
  active = true,
  input,
  title = 'Community chat',
  className,
  sx,
  onClose,
  onLoaded,
  onOpenWheel,
  onOpenCasinoRace,
  onOpenPokerCashTable,
  onOpenPokerTournament,
  onResize,
  onDetach,
  closeOnChatClosed = true,
  pokerLinkCatalog,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const casinoRaceAbortControllerRef = useRef<AbortController | null>(null);
  const casinoRaceDataRef = useRef<CasinoRaceDataResult | null>(null);
  const casinoRaceDebugModeRef = useRef(false);
  const requestIdRef = useRef(0);
  const casinoRaceRequestIdRef = useRef(0);
  const inputKey = useMemo(() => JSON.stringify(input || {}), [input]);
  const [session, setSession] = useState<ChatSessionResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [chatFrameSize, setChatFrameSize] = useState<ChatFrameSize | null>(null);
  const [casinoRaceOpen, setCasinoRaceOpen] = useState(false);
  const [casinoRaceData, setCasinoRaceData] = useState<CasinoRaceDataResult | null>(null);
  const [casinoRaceLoading, setCasinoRaceLoading] = useState(false);
  const [casinoRaceError, setCasinoRaceError] = useState<string | null>(null);

  const chatUrl = useMemo(() => getValidatedChatUrl(session?.chatUrl), [session?.chatUrl]);
  const trustedOrigin = useMemo(() => getOriginFromUrl(chatUrl), [chatUrl]);
  const normalizedPokerLinkCatalog = useMemo(() => normalizePokerLinkCatalog(pokerLinkCatalog), [pokerLinkCatalog]);
  const chatFrameWidth = getBoundedDimension(chatFrameSize?.width, DEFAULT_CHAT_WIDTH, MIN_CHAT_WIDTH, MAX_CHAT_WIDTH);
  const chatFrameHeight = getBoundedDimension(chatFrameSize?.height, DEFAULT_CHAT_HEIGHT, MIN_CHAT_HEIGHT, MAX_CHAT_HEIGHT);
  const showChatFrame = Boolean(chatUrl && !loading && !error);
  const runCasinoLinkAction = useCasinoLinkAction();

  const requestChatSession = useCallback(async () => {
    abortControllerRef.current?.abort();

    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    setSession(null);
    setFrameLoaded(false);
    setChatFrameSize(null);

    try {
      const nextSession = await createChatSession({
        input: buildChatSessionInput(input),
        signal: controller.signal,
      });

      if (controller.signal.aborted || requestIdRef.current !== requestId) return;

      setSession(nextSession);
      if (!getValidatedChatUrl(nextSession.chatUrl)) {
        setError('Community chat URL was not returned.');
      }
    } catch (err) {
      if (controller.signal.aborted || requestIdRef.current !== requestId) return;

      setSession(null);
      setError(err instanceof Error && err.message ? err.message : t('common:errors.genericError'));
    } finally {
      if (!controller.signal.aborted && requestIdRef.current === requestId) {
        setLoading(false);
      }

      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [inputKey]);

  const closeCasinoRacePanel = useCallback(() => {
    casinoRaceAbortControllerRef.current?.abort();
    casinoRaceAbortControllerRef.current = null;
    casinoRaceDataRef.current = null;
    casinoRaceDebugModeRef.current = false;
    setCasinoRaceOpen(false);
    setCasinoRaceLoading(false);
    setCasinoRaceError(null);
    setCasinoRaceData(null);
  }, []);

  const requestCasinoRaceData = useCallback(async (options: CasinoRaceRequestOptions = {}) => {
    casinoRaceDebugModeRef.current = false;

    const showLoading = options.showLoading ?? true;
    const shouldShowLoading = showLoading || !casinoRaceDataRef.current;

    if (!showLoading && casinoRaceAbortControllerRef.current) return;

    casinoRaceAbortControllerRef.current?.abort();

    const controller = new AbortController();
    const requestId = casinoRaceRequestIdRef.current + 1;
    casinoRaceRequestIdRef.current = requestId;
    casinoRaceAbortControllerRef.current = controller;

    setCasinoRaceOpen(true);
    if (shouldShowLoading) {
      setCasinoRaceLoading(true);
    }
    setCasinoRaceError(null);

    try {
      const race = await getCasinoRace({
        slug: 'all-race',
        limit: CASINO_RACE_LEADERBOARD_LIMIT,
        signal: controller.signal,
      });

      if (controller.signal.aborted || casinoRaceRequestIdRef.current !== requestId) return;

      casinoRaceDataRef.current = race;
      setCasinoRaceData(race);
    } catch (err) {
      if (controller.signal.aborted || casinoRaceRequestIdRef.current !== requestId) return;

      if (shouldShowLoading || !casinoRaceDataRef.current) {
        casinoRaceDataRef.current = null;
        setCasinoRaceData(null);
        setCasinoRaceError(err instanceof Error && err.message ? err.message : CASINO_RACE_UNAVAILABLE_MESSAGE);
      }
    } finally {
      if (!controller.signal.aborted && casinoRaceRequestIdRef.current === requestId && shouldShowLoading) {
        setCasinoRaceLoading(false);
      }

      if (casinoRaceAbortControllerRef.current === controller) {
        casinoRaceAbortControllerRef.current = null;
      }
    }
  }, []);

  const postPokerLinkCatalog = useCallback(() => {
    if (!active || !trustedOrigin || !session?.chatSessionId || !iframeRef.current?.contentWindow) return;

    iframeRef.current.contentWindow.postMessage({
      type: 'hs.chat.pokerLinkCatalog',
      chatSessionId: session.chatSessionId,
      cashTables: normalizedPokerLinkCatalog.cashTables,
      tournaments: normalizedPokerLinkCatalog.tournaments,
    }, trustedOrigin);
  }, [active, normalizedPokerLinkCatalog, session?.chatSessionId, trustedOrigin]);

  const handleFrameLoad = useCallback(() => {
    if (!session || loading || error || frameLoaded) return;

    setFrameLoaded(true);
    onLoaded?.(session);
    window.setTimeout(postPokerLinkCatalog, 0);
  }, [error, frameLoaded, loading, onLoaded, postPokerLinkCatalog, session]);

  useEffect(() => {
    if (!active) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      casinoRaceAbortControllerRef.current?.abort();
      casinoRaceAbortControllerRef.current = null;
      setLoading(false);
      setError(null);
      setSession(null);
      setFrameLoaded(false);
      setChatFrameSize(null);
      setCasinoRaceOpen(false);
      setCasinoRaceLoading(false);
      setCasinoRaceError(null);
      casinoRaceDataRef.current = null;
      casinoRaceDebugModeRef.current = false;
      setCasinoRaceData(null);
      return;
    }

    requestChatSession();

    return () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      casinoRaceAbortControllerRef.current?.abort();
      casinoRaceAbortControllerRef.current = null;
    };
  }, [active, requestChatSession]);

  useEffect(() => {
    if (!active || !casinoRaceOpen) return;

    const refreshTimer = window.setInterval(() => {
      if (casinoRaceDebugModeRef.current) return;
      void requestCasinoRaceData({ showLoading: false });
    }, CASINO_RACE_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(refreshTimer);
  }, [active, casinoRaceOpen, requestCasinoRaceData]);

  useEffect(() => {
    if (!IS_CASINO_RACE_DEBUG_ENABLED) return;

    const debugApi: CasinoRaceDebugApi = {
      help: () => {
        console.log('Casino race debug commands:');
        console.log('- hsCasinoRaceDebug.open()');
        console.log('- hsCasinoRaceDebug.refresh()');
        console.log('- hsCasinoRaceDebug.shuffle()');
        return true;
      },
      open: () => {
        void requestCasinoRaceData();
      },
      refresh: () => {
        void requestCasinoRaceData({ showLoading: false });
      },
      shuffle: () => {
        const nextRace = shuffleCasinoRaceForDebug(casinoRaceDataRef.current);
        casinoRaceDataRef.current = nextRace;
        casinoRaceDebugModeRef.current = true;
        setCasinoRaceOpen(true);
        setCasinoRaceLoading(false);
        setCasinoRaceError(null);
        setCasinoRaceData(nextRace);
        return nextRace;
      },
    };

    const debugWindow = window as typeof window & { hsCasinoRaceDebug?: CasinoRaceDebugApi };
    debugWindow.hsCasinoRaceDebug = debugApi;

    return () => {
      if (debugWindow.hsCasinoRaceDebug === debugApi) {
        delete debugWindow.hsCasinoRaceDebug;
      }
    };
  }, [requestCasinoRaceData]);

  useEffect(() => {
    if (!active || !session || error) return;

    const refreshDelayMs = getSessionRefreshDelayMs(session);
    if (refreshDelayMs === null) return;

    const refreshTimer = window.setTimeout(() => {
      requestChatSession();
    }, refreshDelayMs);

    return () => window.clearTimeout(refreshTimer);
  }, [active, error, requestChatSession, session]);

  useEffect(() => {
    if (!active || !chatUrl || loading || error || frameLoaded) return;

    const readyTimer = window.setTimeout(() => {
      setError(CHAT_UNAVAILABLE_MESSAGE);
    }, CHAT_EMBED_READY_TIMEOUT_MS);

    return () => window.clearTimeout(readyTimer);
  }, [active, chatUrl, error, frameLoaded, loading]);

  useEffect(() => {
    if (!active || !trustedOrigin) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== trustedOrigin) return;

      const message = parseChatMessage(event.data);
      if (!message) return;
      if (message.chatSessionId && session?.chatSessionId && message.chatSessionId !== session.chatSessionId) return;

      if (message.type === 'hs.chat.loaded') {
        if (!message.chatSessionId || !session?.chatSessionId || message.chatSessionId !== session.chatSessionId) return;
        setFrameLoaded(true);
        onLoaded?.(session);
        postPokerLinkCatalog();
        return;
      }

      if (message.type === 'hs.chat.closed') {
        if (!closeOnChatClosed) return;
        onClose?.();
        return;
      }

      if (message.type === 'hs.chat.openWheel') {
        if (!message.chatSessionId || !session?.chatSessionId || message.chatSessionId !== session.chatSessionId) return;
        onOpenWheel?.();
        return;
      }

      if (message.type === 'hs.chat.openCasinoRace') {
        if (!message.chatSessionId || !session?.chatSessionId || message.chatSessionId !== session.chatSessionId) return;
        requestCasinoRaceData();
        onOpenCasinoRace?.();
        return;
      }

      if (message.type === 'hs.chat.openPokerCashTable') {
        if (!message.chatSessionId || !session?.chatSessionId || message.chatSessionId !== session.chatSessionId) return;
        if (!message.roomId) return;
        onOpenPokerCashTable?.(message.roomId);
        return;
      }

      if (message.type === 'hs.chat.openPokerTournament') {
        if (!message.chatSessionId || !session?.chatSessionId || message.chatSessionId !== session.chatSessionId) return;
        if (!message.gameId) return;
        onOpenPokerTournament?.(message.gameId);
        return;
      }

      if (message.type === 'hs.chat.casinoLink') {
        if (!message.chatSessionId || !session?.chatSessionId || message.chatSessionId !== session.chatSessionId) return;
        if (!message.casinoAction) return;

        void runCasinoLinkAction({ action: message.casinoAction }).catch((err) => {
          console.error('Chat casino link action failed', err);
        });
        return;
      }

      if (message.type === 'hs.chat.detach') {
        if (!message.chatSessionId || !session?.chatSessionId || message.chatSessionId !== session.chatSessionId) return;
        onDetach?.();
        return;
      }

      if (message.type === 'hs.chat.resize') {
        const nextSize = {
          width: message.width ?? chatFrameSize?.width,
          height: message.height ?? chatFrameSize?.height,
        };
        setChatFrameSize(nextSize);
        onResize?.(nextSize);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [active, chatFrameSize, closeOnChatClosed, onClose, onLoaded, onOpenCasinoRace, onOpenPokerCashTable, onOpenPokerTournament, onOpenWheel, onResize, postPokerLinkCatalog, requestCasinoRaceData, runCasinoLinkAction, session, trustedOrigin]);

  useEffect(() => {
    if (!active || !frameLoaded) return;

    postPokerLinkCatalog();
  }, [active, frameLoaded, postPokerLinkCatalog]);

  if (!active) return null;

  return (
    <Box
      className={className}
      aria-busy={loading || casinoRaceLoading}
      sx={[
        {
          position: 'relative',
          width: `min(${chatFrameWidth}px, 100%)`,
          height: `min(${chatFrameHeight}px, calc(100vh - 32px))`,
          minWidth: 0,
          minHeight: showChatFrame ? MIN_CHAT_HEIGHT : 260,
          bgcolor: '#2A2A32',
          color: '#FFFFFF',
          // border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '4px',
          overflow: 'hidden',
          boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
          boxSizing: 'border-box',
          '@media (max-width: 600px)': {
            width: '100%',
            height: `min(${chatFrameHeight}px, calc(100vh - 16px))`,
            minHeight: 480,
            borderRadius: 0,
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {loading ? (
        <Stack sx={{ height: '100%', p: 2, boxSizing: 'border-box' }} alignItems="center" justifyContent="center" spacing={2}>
          <CircularProgress size={28} />
          <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 14 }}>
            Opening community chat...
          </Typography>
        </Stack>
      ) : error ? (
        <Stack sx={{ height: '100%', p: 2, boxSizing: 'border-box' }} alignItems="stretch" justifyContent="center" spacing={2}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" onClick={requestChatSession}>
            Retry
          </Button>
        </Stack>
      ) : chatUrl ? (
        <>
          <iframe
            ref={iframeRef}
            key={`${session?.chatSessionId || 'chat'}:${chatUrl}`}
            title={title}
            src={chatUrl}
            onLoad={handleFrameLoad}
            style={{ display: 'block', width: '100%', height: '100%', border: 0 }}
            allow="microphone *; clipboard-read; clipboard-write; fullscreen"
            referrerPolicy="strict-origin-when-cross-origin"
          />
          {!frameLoaded && (
            <Stack
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: '#191A1F',
                pointerEvents: 'auto',
              }}
              alignItems="center"
              justifyContent="center"
              spacing={2}
            >
              <CircularProgress size={26} />
              <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 14 }}>
                Loading community chat...
              </Typography>
            </Stack>
          )}
        </>
      ) : (
        <Stack sx={{ height: '100%', p: 3 }} alignItems="center" justifyContent="center">
          <Alert severity="error">Community chat URL was not returned.</Alert>
        </Stack>
      )}

      {casinoRaceOpen && (
        <CasinoRacePanel
          race={casinoRaceData}
          loading={casinoRaceLoading}
          error={casinoRaceError}
          onClose={closeCasinoRacePanel}
          onRetry={requestCasinoRaceData}
        />
      )}
    </Box>
  );
};

export default CommunityChatPanel;
